import Settings from './domain/Settings';
import prebid from './prebid';
import prebidService from './prebid/prebidService';
import * as performanceService from './services/performanceService';
import * as adManager from './services/adManager';
import breakpoints from './services/breakpoints';
import closeableAdManager from './services/closeableAdManager';
import conditionalService from './services/conditionalService';
import lazyLoadService from './services/lazyLoadService';
import './services/messaging';
import * as privacyService from './services/privacyService';
import * as userService from './services/userService';
import * as experimentService from './services/experimentService';
import refreshManager from './services/refreshManager';
import sizeMappings from './services/sizeMappings';
import * as slotManager from './services/slotManager';
import * as targeting from './services/targeting';
import cmd from './utils/cmd';
import './utils/debug';
import hooks from './utils/hooks';
import perf from './utils/performance';
import pubsub from './utils/pubsub';

let settings = null;

if (window.advert && typeof window.advert.init === 'function') {
	throw new Error('[ADVERT] web advertising script already loaded');
}

function init(config, userHooks) {
	cmd.reject('loaded');

	settings = new Settings(config.xandr, config.debugInfo ?? {});

	performanceService.init();
	experimentService.init(config.experiments);

	settings = new Settings(conditionalService.applyConditionalConfiguration(config.xandr), config.debugInfo ?? {});

	_coreInit(userHooks);

	privacyService.init();
	userService.init();

	return new Promise((res) => {
		privacyService.onConsent(async () => {
			if (privacyService.hasXandrConsent() || Settings.getInstance().features?.xandrSimpleAds) {
				await _prebidInit(config.prebid);
				await _xandrInit();

				cmd.accept('loaded');
			}

			res();
		});
	});
}

function _coreInit(userHooks = {}) {
	perf.mark('core - initialisation - start');

	hooks.init(userHooks);
	window.advert.addHook = hooks.add;
	window.advert.removeHook = hooks.remove;

	perf.mark('core - initialisation - end');
}

async function _prebidInit(config) {
	await prebid.init(config);
}

async function _xandrInit() {
	perf.mark('xandr - initialisation - start');

	lazyLoadService.init(settings);
	breakpoints.init(settings);
	sizeMappings.init(settings); // Depends on breakpoints
	refreshManager.init(settings);
	await adManager.init(); // Depends on sizeMappings & breakpoints
	await slotManager.init(settings); // Depends on ad manager
	closeableAdManager.init(slotManager);

	await pubsub.publish('settings.changed');

	perf.mark('xandr - initialisation - end');
}

const initialCmds = window.advert && window.advert.cmd || [];

window.advert = Object.assign({}, window.advert, {
	init,
	pubsub,
	cmd,
	'loadSlot': slotManager.loadSlot,
	'getSlot': slotManager.getSlot,
	'getSlots': slotManager.getSlots,
	'defineSlots': slotManager.defineSlots,
	'refreshSlots': adManager.refreshSlots,
	'getCurrentBreakpoint': breakpoints.getCurrentBreakpoint,
	'getSizeMapping': sizeMappings.getSizeMapping,
	'getSizesFromSizeMapForBreakpoint': sizeMappings.getSizesFromSizeMapForBreakpoint,
	'getBidResults': prebidService.getBidResults,
	'getPageTargeting': targeting.getPageTargeting,
	'getSlotTargeting': targeting.getSlotTargeting,
	'getSettings': () => settings,
	'updatePageTargeting': (pageTargeting) => {
		targeting.setPageTargeting(pageTargeting);
		adManager.updatePageTargeting();
	},
	'getSlotNameFromMapping': slotManager.getSlotNameFromMapping,
	'prebid': {
		'getSettings': prebid.getSettings
	},
	'getActiveExperiments': experimentService.getActiveExperiments
});

cmd.setup(initialCmds, ['ready']);

perf.mark('core - ready');
